/**
 * @fileoverview Custom hook for fetching facilities data.
 */

import {useQuery} from "react-query";
import Alert from "react-s-alert";

import {FacilityService} from "../services/FacilityService";
import {Page} from "../services/api/pagination";
import {FacilityModel} from "../utils/types/facility";
import {isUnauthorized} from "../services/api";

/**
 * Query name for facilities data.
 */
export const FacilitiesQueryName = "Facilities";

/**
 * Props for the useFacilitiesQuery hook.
 */
type UseFacilitiesQueryProps = {
  enabled: boolean;
  onSuccess: (data: Page<FacilityModel> | undefined) => void;
};

/**
 * Custom hook for fetching facilities data.
 * Fetches all facilities when the user is logged in and updates Redux store on success.
 *
 * @param {UseFacilitiesQueryProps} params The hook parameters.
 * @param {boolean} params.enabled Whether the query should be enabled.
 * @param {Function} params.onSuccess Callback function to handle successful fetch.
 * @returns {Object} React Query result object.
 */
export const useFacilitiesQuery = ({
  enabled,
  onSuccess,
}: UseFacilitiesQueryProps) =>
  useQuery(
    FacilitiesQueryName,
    async () => {
      const {data} = await FacilityService.getAll();
      return data;
    },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      enabled,
      onSuccess,
      onError: (error: unknown) => {
        const status = (error as {status?: number})?.status;
        if (!isUnauthorized(status)) {
          Alert.error("Failed to refresh facilities.");
        }
      },
    },
  );
